import { EnvironmentProviders, inject, Provider } from '@angular/core';
import {
  provideSoftAppsModule,
  provideSoftAppsRoutes,
  withSoftAppsModuleInfo,
  withSoftAppsModuleTranslation,
} from '@softapps/core';
import { getModuleGroupByName } from '@softline/application';
import { default as de } from '../i18n/de.json';
import { PmsRosterEventRepository } from './repositories/event/pms-roster-event.repository';
import { RosterEventRepository } from './repositories/event/roster-event.repository';
import { PmsRosterResourceRepository } from './repositories/resource/pms-roster-resource.repository';
import { RosterResourceRepository } from './repositories/resource/roster-resource.repository';
import { PmsRosterStructureRepository } from './repositories/structure/pms-roster-structure.repository';
import { RosterStructureRepository } from './repositories/structure/roster-structure.repository';
import { rosterRoutes } from './roster.routes';
import { RosterFeature, SOFTLINE_TOKEN_ROSTER_FEATURE } from './roster.shared';
import { PmsService } from './services/pms.service';
import { RosterFeatureRegistry } from './services/roster-feature.registry';
import { RosterService } from './services/roster.service';
import { RosterContent } from './types/roster-content';

/**
 * Registers one roster feature (tile + translations + registry entry). Callable from
 * a separately deployed federation remote — requires `@softapps/pms/roster` to be a
 * shared mapping so `SOFTLINE_TOKEN_ROSTER_FEATURE` keeps one identity.
 */
export function provideRosterFeature(
  feature: RosterFeature<any>
): Array<Provider | EnvironmentProviders> {
  if (!feature.module) return [];

  return [
    provideSoftAppsModule(
      {
        name: feature.module.name,
        title: feature.module.title,
        icon: feature.module.icon ?? 'fa-regular fa-calendar-users',
        route: `/roster/${feature.name}`,
        color: feature.module.color,
        group: feature.module.group,
        permission: feature.module.permission,
      },
      withSoftAppsModuleTranslation(feature?.translations ?? {}),
      withSoftAppsModuleInfo(feature.info ?? {})
    ),
    {
      provide: SOFTLINE_TOKEN_ROSTER_FEATURE,
      multi: true,
      useValue: feature,
    },
  ];
}

export function provideRoster(
  ...features: RosterFeature<any>[]
): Array<Provider | EnvironmentProviders> {
  return [
    ...features.flatMap((feature) => provideRosterFeature(feature)),
    ...provideSoftAppsRoutes({
      app: rosterRoutes,
    }),
    {
      provide: RosterFeatureRegistry,
      useFactory: () => new RosterFeatureRegistry(inject(SOFTLINE_TOKEN_ROSTER_FEATURE)),
    },
  ];
}

export function withPmsRoster(): RosterFeature<RosterContent> {
  return {
    name: 'pms',
    module: {
      name: 'roster-pms',
      title: 'Dienstplaner',
      icon: 'fa-regular fa-calendar-days',
      color: 'bg-primary text-white',
      group: getModuleGroupByName('Personalmanagement'),
    },
    info: {
      library: '@softapps/pms/roster',
      description: 'Dienstplanung der Mitarbeiter im Kalender.',
    },
    providers: [
      { provide: RosterService, useClass: PmsService },
      { provide: RosterEventRepository, useClass: PmsRosterEventRepository },
      { provide: RosterResourceRepository, useClass: PmsRosterResourceRepository },
      { provide: RosterStructureRepository, useClass: PmsRosterStructureRepository },
    ],
    translations: { de },
    components: {
      eventComponent: () =>
        import('./components/pms-components/pms-event/pms-event.component').then(
          (o) => o.PmsEventComponent
        ),
      calendarResourceComponent: () =>
        import('./components/pms-components/pms-resource/pms-resource.component').then(
          (o) => o.PmsResourceComponent
        ),
      resourceComponent: () =>
        import('./components/pms-components/pms-tasks/pms-tasks.component').then(
          (o) => o.PmsTasksComponent
        ),
    },
  };
}
